/**
 * DecryptTextUseCase
 *
 * Pure-domain wrapper around `textEncryptionService.decrypt`. Pair-of-three
 * with `InitializeTextEncryptionUseCase` and `EncryptTextUseCase`. Callers
 * (use cases that own a chat read hop — `FetchChatMessagesUseCase` and
 * `SubscribeToChatUpdatesUseCase`) invoke
 * `decryptTextUseCase.execute(ciphertext)` instead of threading a
 * `decryptContent` callback or reaching into the service directly.
 *
 * Pre-condition: the singleton's AES-GCM key has been derived for the
 * intended peer via `InitializeTextEncryptionUseCase.execute`. When the key
 * is null (never-initialized, peer had no `publicJwk`, or derivation failed)
 * the service returns the input unchanged — Flutter parity:
 * `e2ee_impl.dart:92-94`.
 *
 * Flutter parity: both chat read paths (paginated fetch and live subscription)
 * invoke `e2ee.decrypt(content)` exactly once when `content` is non-null — the
 * empty-string branch is NOT short-circuited at the Flutter call site; the
 * service no-ops via the pass-through return. The `chat_screen.dart` anchors
 * for those two call sites, and the web's narrower eligibility gate, are
 * documented once on `ChatUtils.shouldDecryptMessageContent`.
 */

import {
  TextEncryptionService,
  textEncryptionService,
} from '@/domain/services/textEncryption/textEncryptionService';

export class DecryptTextUseCase {
  private textEncryptionService: TextEncryptionService;

  constructor(service: TextEncryptionService = textEncryptionService) {
    this.textEncryptionService = service;
  }

  /**
   * Decrypt `ciphertext` using the singleton's currently-derived AES-GCM key.
   * Consumes one UTF-16 code unit per byte (matches Flutter's
   * `message.codeUnits` read path). When the derived key is null
   * (pass-through mode) returns `ciphertext` unchanged.
   */
  async execute(ciphertext: string): Promise<string> {
    return this.textEncryptionService.decrypt(ciphertext);
  }
}

export const decryptTextUseCase = new DecryptTextUseCase();
